import { Flex } from '@mantine/core';
import { UserHeader } from '@widgets/user';
import { VacancyList } from '@widgets/vacancy';
import { UserEditForm, type UserFormValues } from '../userEditForm';
import { useShowMe } from '@entities/auth';
import { useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateUser, type User } from '@entities/user';
import { ROLES } from '@shared/constants';
import { showNotification } from '@shared/utils';
import { QueryKey } from '@shared/config';

type UserEditProps = {
  user: User;
};

export const UserEdit = ({ user }: UserEditProps) => {
  const { id } = useParams();
  const { data: me } = useShowMe();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutate: updateUser, isPending } = useUpdateUser(id || '');

  const handleSubmit = (values: UserFormValues) => {
    updateUser(
      {
        email: values.email,
        role: values.role,
        profile: {
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone || '',
          avatarId: values?.avatarId || user.profile.avatar?.id || null,
          position: user.profile.position,
        },
        companyId: Number(values.companyId) || undefined,
      },
      {
        onSuccess: data => {
          queryClient.invalidateQueries({
            queryKey: [QueryKey.GET_USERS_WITH_PAGINATION],
          });

          queryClient.setQueryData([QueryKey.GET_USER, id], data);

          navigate(`/user-list/${data?.id}`);
        },
        onError: error => {
          showNotification({
            error,
            message:
              error.response?.data.message ||
              'Ошибка при обновлении пользователя',
            variant: 'error',
          });
        },
      }
    );
  };

  const handleClose = () => {
    navigate('/');
  };

  return (
    <Flex
      wrap='wrap'
      direction='column'
      gap={32}
      pt={16}
      mx='auto'
      maw={1512}
      w='100%'
    >
      <UserHeader
        isDelete={false}
        createdAt={user.createdAt}
        id={user.id}
        firstName={user.profile.firstName}
        lastName={user.profile.lastName}
        group={user.role.displayName}
        company={user?.company?.name}
      />

      <Flex
        wrap='wrap'
        gap={24}
        align='flex-start'
        direction={{ base: 'column', md: 'row' }}
      >
        <Flex direction='column' gap={24} flex={1} w='100%' maw={1070}>
          <UserEditForm
            isAdmin={me?.role.name === ROLES.ADMIN}
            isLoading={isPending}
            isDisabled={isPending}
            handleSubmit={handleSubmit}
            handleClose={handleClose}
            initialValues={{
              avatarId: user.profile.avatar?.id || null,
              firstName: user.profile.firstName,
              lastName: user.profile.lastName,
              email: user.email,
              phone: user.profile.phone,
              role: user.role.name,
              companyId: user.company?.id.toString(),
            }}
          />
        </Flex>

        <Flex direction='column' gap={24}>
          <VacancyList
            title='Вакансии'
            vacancies={user.vacancies.slice(0, 3)}
            totalVacancies={user.vacancies.length}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};
